import { ofType } from 'redux-observable';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { AxiosError } from 'axios';

import { CustomEpic, ErrorActionPayload } from 'types/types';
import {
  FETCH_GOOGLE_OAUTH2_AUTHORIZATION_CODE,
  FETCH_GOOGLE_OAUTH2_TOKENS,
} from '../actionTypes';
import { GOOGLE_AUTHORIZATION_CODE_ENDPOINT } from '../constants';
import { handleAjaxSuccess } from 'utils/epics/handleAjaxSuccess';
import {
  fetchGoogleOauth2TokensFailure,
  fetchGoogleOauth2TokensSuccess,
  fetchGoogleOauth2TokensUnauthorized,
} from '../actions';
import { loadGoogleIdentityServices } from 'utils/loadGoogleIdentityServices';

const googleUnavailableFailure = () =>
  fetchGoogleOauth2TokensFailure({
    message:
      'Google authentication is not available. Please disable your ad blocker or check your internet connection and try again.',
    name: 'GoogleIdentityServicesUnavailable',
  });

export const fetchAuthorizationCode$: CustomEpic<undefined> = action$ =>
  action$.pipe(
    ofType(FETCH_GOOGLE_OAUTH2_AUTHORIZATION_CODE),
    switchMap(() =>
      from(loadGoogleIdentityServices()).pipe(
        switchMap(() => {
          if (typeof google === 'undefined' || !google.accounts?.oauth2) {
            return of(googleUnavailableFailure());
          }

          const client = google.accounts.oauth2.initCodeClient({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
            scope:
              'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid',
            ux_mode: 'redirect',
            redirect_uri: import.meta.env.VITE_GOOGLE_OAUTH2_REDIRECT_URL || '',
            state: import.meta.env.VITE_GOOGLE_OAUTH2_STATE || '',
          });
          client.requestCode();
          return of({ type: 'FETCH_GOOGLE_OAUTH2_AUTHORIZATION_CODE_STARTED' });
        }),
        catchError(() => of(googleUnavailableFailure())),
      ),
    ),
  );

type FetchGoogleOauth2TokensPayload = {
  authorizationCode: string;
};

export const fetchGoogleOauth2Tokens$: CustomEpic<
  FetchGoogleOauth2TokensPayload
> = (action$, state$, { postApi$ }) =>
  action$.pipe(
    ofType(FETCH_GOOGLE_OAUTH2_TOKENS),
    switchMap(({ payload: { authorizationCode } }) => {
      return postApi$({
        endpoint: GOOGLE_AUTHORIZATION_CODE_ENDPOINT,
        payload: { code: authorizationCode },
        headers: {},
      }).pipe(
        handleAjaxSuccess(fetchGoogleOauth2TokensSuccess),
        catchError((err: AxiosError) => {
          const errorActionPayload: ErrorActionPayload = {
            message: err?.message,
            name: err?.name,
            status: err?.response?.status,
          };
          const failActionCreator =
            err?.response?.status === 401
              ? fetchGoogleOauth2TokensUnauthorized
              : fetchGoogleOauth2TokensFailure;
          return [failActionCreator(errorActionPayload)];
        }),
      );
    }),
  );
